import { useParams, Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ExternalLink, Apple, Smartphone, Globe } from "lucide-react";
import ToolCard from "@/components/ToolCard";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Tool } from "@/data/tools";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import MetaTags from "@/components/MetaTags";
import useAnalytics from '../hooks/useAnalytics';
import ShareButton from "@/components/ShareButton";

interface DbTool {
  id: string;
  name: string;
  description: string;
  seo_description: string | null;
  visit_url: string;
  url_slug: string;
  approved: boolean;
  featured: boolean;
  logo_url: string | null;
  ios_store_url: string | null;
  android_store_url: string | null;
  sports: { id: string; name: string }[];
  platforms: { id: string; name: string }[];
  categories: { id: string; name: string }[];
}

const ToolDetails = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [tool, setTool] = useState<DbTool | null>(null);
  const [similarTools, setSimilarTools] = useState<DbTool[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { trackEvent, trackAppView } = useAnalytics();

  const getSlug = (name: string) => {
    return encodeURIComponent(name.toLowerCase().replace(/\s+/g, '-'));
  };

  const formatDescription = (description: string) => {
    const withBullets = description.replace(/•\s+/g, '• ');

    const parts = withBullets.split('• ');
    
    if (parts.length <= 1) {
      return description.split('\n').map((paragraph, i) => (
        <p key={i} className="mb-2">
          {paragraph}
        </p>
      ));
    }
    
    const intro = parts[0];
    const bullets = parts.slice(1);
    
    return (
      <>
        {intro && <p className="mb-4">{intro}</p>}
        <ul className="list-disc pl-5 space-y-2 mb-4">
          {bullets.map((bullet, index) => (
            <li key={index} className="text-gray-600">{bullet}</li>
          ))}
        </ul>
      </>
    );
  };

  useEffect(() => {
    const fetchToolDetails = async () => {
      setIsLoading(true);
      try {
        const { data, error } = await supabase
          .from('apps')
          .select(`
            id, 
            name, 
            description,
            seo_description, 
            visit_url,
            url_slug,
            approved,
            featured,
            logo_url,
            ios_store_url,
            android_store_url,
            app_sports(
              sport_id,
              sports(id, name)
            ),
            app_platforms(
              platform_id,
              platforms(id, name)
            ),
            app_categories(
              category_id,
              categories(id, name)
            )
          `)
          .eq('url_slug', slug)
          .eq('approved', true)
          .single();

        if (error) throw error;

        if (!data) {
          setError("Tool not found");
          setIsLoading(false);
          return;
        }

        const transformedTool: DbTool = {
          id: data.id,
          name: data.name,
          description: data.description,
          seo_description: data.seo_description,
          visit_url: data.visit_url,
          url_slug: data.url_slug,
          approved: data.approved,
          featured: data.featured,
          logo_url: data.logo_url,
          ios_store_url: data.ios_store_url,
          android_store_url: data.android_store_url,
          sports: data.app_sports.map((item: any) => item.sports),
          platforms: data.app_platforms.map((item: any) => item.platforms),
          categories: data.app_categories.map((item: any) => item.categories),
        };

        // Set the tool data but don't manipulate meta tags directly
        // Let the MetaTags component handle this in the render method
        setTool(transformedTool);

        if (transformedTool.categories.length > 0 || transformedTool.sports.length > 0) {
          await fetchSimilarTools(transformedTool);
        }

        trackAppView(
          transformedTool.id, 
          transformedTool.name, 
          transformedTool.categories.length > 0 ? transformedTool.categories[0].name : 'unknown'
        );
      } catch (error) {
        console.error("Error fetching tool details:", error);
        setError("Failed to load tool details");
      } finally {
        setIsLoading(false);
      }
    };

    if (slug) {
      fetchToolDetails();
    }
  }, [slug, navigate, trackAppView]);

  const fetchSimilarTools = async (currentTool: DbTool) => {
    try {
      const categoryIds = currentTool.categories.map(cat => cat.id);
      const sportIds = currentTool.sports.map(sport => sport.id);
      
      if (categoryIds.length > 0) {
        const { data: categoryAppData, error: categoryAppError } = await supabase
          .from('app_categories')
          .select('app_id')
          .in('category_id', categoryIds)
          .neq('app_id', currentTool.id);
          
        if (categoryAppError) {
          console.error("Error fetching similar apps by category:", categoryAppError);
        } else if (categoryAppData && categoryAppData.length > 0) {
          const uniqueAppIds = [...new Set(categoryAppData.map(item => item.app_id))];
          
          const { data: categoryApps, error: categoryAppsError } = await supabase
            .from('apps')
            .select(`
              id, 
              name, 
              description, 
              visit_url,
              url_slug,
              approved,
              featured,
              logo_url,
              ios_store_url,
              android_store_url,
              app_sports(
                sport_id,
                sports(id, name)
              ),
              app_platforms(
                platform_id,
                platforms(id, name)
              ),
              app_categories(
                category_id,
                categories(id, name)
              )
            `)
            .in('id', uniqueAppIds)
            .eq('approved', true)
            .limit(3);

          if (categoryAppsError) {
            console.error("Error fetching similar tools by category:", categoryAppsError);
          } else if (categoryApps && categoryApps.length > 0) {
            const transformedSimilarTools = transformAppsData(categoryApps);
            setSimilarTools(transformedSimilarTools);
            return;
          }
        }
      }
      
      if (sportIds.length > 0) {
        const { data: sportAppData, error: sportAppError } = await supabase
          .from('app_sports')
          .select('app_id')
          .in('sport_id', sportIds)
          .neq('app_id', currentTool.id);
          
        if (sportAppError) {
          console.error("Error fetching similar apps by sport:", sportAppError);
        } else if (sportAppData && sportAppData.length > 0) {
          const uniqueAppIds = [...new Set(sportAppData.map(item => item.app_id))];
          
          const { data: sportApps, error: sportAppsError } = await supabase
            .from('apps')
            .select(`
              id, 
              name, 
              description, 
              visit_url,
              url_slug,
              approved,
              featured,
              logo_url,
              ios_store_url,
              android_store_url,
              app_sports(
                sport_id,
                sports(id, name)
              ),
              app_platforms(
                platform_id,
                platforms(id, name)
              ),
              app_categories(
                category_id,
                categories(id, name)
              )
            `)
            .in('id', uniqueAppIds)
            .eq('approved', true)
            .limit(3);

          if (sportAppsError) {
            console.error("Error fetching similar tools by sport:", sportAppsError);
          } else if (sportApps && sportApps.length > 0) {
            const transformedSimilarTools = transformAppsData(sportApps);
            setSimilarTools(transformedSimilarTools);
          }
        }
      }
    } catch (error) {
      console.error("Error fetching similar tools:", error);
    }
  };

  const transformAppsData = (appsData: any[]): DbTool[] => {
    return appsData.map(item => ({
      id: item.id,
      name: item.name,
      description: item.description,
      seo_description: item.seo_description,
      visit_url: item.visit_url,
      url_slug: item.url_slug,
      approved: item.approved,
      featured: item.featured,
      logo_url: item.logo_url,
      ios_store_url: item.ios_store_url,
      android_store_url: item.android_store_url,
      sports: item.app_sports.map((s: any) => s.sports),
      platforms: item.app_platforms.map((p: any) => p.platforms),
      categories: item.app_categories.map((c: any) => c.categories),
    }));
  };

  const convertToToolCardFormat = (dbTool: DbTool): Tool => {
    return {
      id: dbTool.id,
      name: dbTool.name,
      logo: dbTool.logo_url || "https://placehold.co/60x60",
      rating: 4.5,
      reviews: 100,
      pricing: "Free Trial",
      description: dbTool.description,
      tags: [],
      category: dbTool.categories.length > 0 ? dbTool.categories[0].name : "",
      sports: dbTool.sports.map(sport => sport.name),
      platforms: dbTool.platforms.map(platform => platform.name),
      featured: dbTool.featured,
      visitUrl: dbTool.visit_url,
      bookmarks: 0,
      url_slug: dbTool.url_slug,
    };
  };

  if (isLoading) {
    const loadingTitle = "Loading Tool... - FitnessAppSearch";
    const currentUrl = window.location.href;
    return (
      <>
        <MetaTags 
          title={loadingTitle}
          description="Loading tool details..."
          url={currentUrl}
        />
        <Navigation />
        <div className="container py-8">
          <p>Loading tool details...</p>
        </div>
        <Footer />
      </>
    );
  }

  if (error || !tool) {
    useEffect(() => {
      const timer = setTimeout(() => {
        navigate('/not-found', { replace: true });
      }, 2000);
      
      return () => clearTimeout(timer);
    }, [navigate]);

    const errorTitle = "Tool Not Found - FitnessAppSearch";
    const currentUrl = window.location.href;
    return (
      <>
        <MetaTags 
          title={errorTitle}
          description="The requested tool could not be found."
          url={currentUrl}
        />
        <Navigation />
        <div className="container py-8">
          <p>{error || "Tool not found"}</p>
          <p>Redirecting to 404 page...</p>
          <Link to="/" className="text-blue-600 hover:underline mt-4 inline-block">
            ← Back to all tools
          </Link>
        </div>
        <Footer />
      </>
    );
  }

  const pageTitle = `${tool.name} - FitnessAppSearch`;
  const pageDescription = tool.seo_description || tool.description?.substring(0, 160);
  const canonicalUrl = `${window.location.origin}/tool/${tool.url_slug}`;
  const imageUrl = tool.logo_url ? 
    (tool.logo_url.startsWith('http') ? tool.logo_url : `${window.location.origin}${tool.logo_url}`) : 
    `${window.location.origin}/default-app-logo.png`;

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <MetaTags 
        title={pageTitle}
        description={pageDescription}
        image={imageUrl}
        url={canonicalUrl}
      />
      <Navigation />
      <div className="container py-8 flex-grow">
        <Link to="/" className="text-blue-600 hover:underline mb-4 inline-block">
          ← Back to all tools
        </Link>
        
        <div className="bg-white rounded-lg border p-6 mt-4">
          <div className="flex items-start gap-6">
            <img 
              src={tool.logo_url || "https://placehold.co/60x60"} 
              alt={tool.name} 
              className="w-20 h-20 rounded-lg object-contain bg-gray-50" 
            />
            
            <div className="flex-1">
              <h1 className="text-3xl font-bold mb-2">{tool.name}</h1>
              <div className="text-gray-600 text-lg mb-4">
                {formatDescription(tool.description)}
              </div>
              
              <div className="flex flex-wrap gap-2 mb-4">
                {tool.categories.map((category) => (
                  <Link 
                    key={category.id} 
                    to={`/category/${getSlug(category.name)}`}
                    className="px-2 py-1 bg-gray-100 text-gray-800 rounded-full text-xs font-medium hover:bg-gray-200 transition-colors"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {category.name}
                  </Link>
                ))}
                {tool.sports.map((sport) => (
                  <Link 
                    key={sport.id} 
                    to={`/sport/${getSlug(sport.name)}`}
                    className="px-2 py-1 bg-[#F97316] text-white rounded-full text-xs font-medium hover:bg-[#EA580C] transition-colors"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {sport.name}
                  </Link>
                ))}
                {tool.platforms.map((platform) => (
                  <Link 
                    key={platform.id} 
                    to={`/platform/${getSlug(platform.name)}`}
                    className="px-2 py-1 bg-[#E5DEFF] text-[#6B4EFF] rounded-full text-xs font-medium hover:bg-[#D4CAFF] transition-colors"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {platform.name}
                  </Link>
                ))}
              </div>

              <div className="flex flex-wrap gap-3">
                <Button
                  className="flex items-center gap-2"
                  onClick={() => window.open(tool.visit_url, '_blank')}
                >
                  <Globe className="h-4 w-4" />
                  Visit Website
                </Button>

                {tool.ios_store_url && (
                  <Button
                    variant="outline"
                    className="flex items-center gap-2"
                    onClick={() => window.open(tool.ios_store_url!, '_blank')}
                  >
                    <Apple className="h-4 w-4" />
                    App Store
                  </Button>
                )}

                {tool.android_store_url && (
                  <Button
                    variant="outline"
                    className="flex items-center gap-2"
                    onClick={() => window.open(tool.android_store_url!, '_blank')}
                  >
                    <Smartphone className="h-4 w-4" />
                    Google Play
                  </Button>
                )}
                
                <ShareButton title={`Check out ${tool.name}`} />
              </div>
            </div>
          </div>
        </div>

        {similarTools.length > 0 && (
          <div className="mt-12">
            <h2 className="text-2xl font-semibold mb-6">Similar Tools</h2>
            <div className="space-y-2 bg-white rounded-lg border">
              {similarTools.map((similarTool) => (
                <ToolCard 
                  key={similarTool.id} 
                  tool={convertToToolCardFormat(similarTool)} 
                />
              ))}
            </div>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default ToolDetails;
